import { useCallback } from 'react';
import { useRxCollection, useRxData } from 'rxdb-hooks';
import { v4 as uuidv4 } from 'uuid';

import { useMst } from '../../mobx-models/Root';
import { sentryCaptureException } from '../../utilities/sentryCaptureException';
import { getValueFromLocalStorage, isNull } from '../../utilities/utilityFunctions';
import { RxdbCollectionName } from '../rxdbCollectionName';
import type { SavepmChecklist, SavePmChecklistCollection } from './savepmChecklist.types';

const useSavePmChecklist = () => {
  const { SiteDiscipline, CurrentUserSelected } = useMst();
  const savePmChecklistCollection: SavePmChecklistCollection = useRxCollection(
    RxdbCollectionName.SAVE_PMCHECKLIST,
  );

  const getPmChecklistQuery = useCallback(
    (collection: SavePmChecklistCollection) =>
      collection?.find({
        selector: { fkAssetInspectionTask: CurrentUserSelected.taskId, isActive: true },
      }),
    [CurrentUserSelected.taskId],
  );

  const { result } = useRxData<SavepmChecklist>(
    RxdbCollectionName.SAVE_PMCHECKLIST,
    getPmChecklistQuery,
  );

  const savePmChecklistTransactionData = async (PMChecklistPayload: SavepmChecklist) => {
    try {
      const newDate = new Date();
      let saveResult = await savePmChecklistCollection
        ?.findOne({
          selector: {
            pk_uuid: PMChecklistPayload.pk_uuid,
          },
        })
        .update({
          $set: {
            isCompleted: PMChecklistPayload.isCompleted,
            comments: PMChecklistPayload.comments,
            dateModified: newDate.toISOString(),
          },
        });
      if (saveResult === null) {
        const savePmChecklistRecord: SavepmChecklist = {
          inspectionId: CurrentUserSelected.inspectionId,
          assetCategoryInspectionTypeCheckListId: isNull(
            PMChecklistPayload,
            'assetCategoryInspectionTypeCheckListId',
            0,
          ),
          isCompleted:
            PMChecklistPayload.isCompleted === null ? null : PMChecklistPayload.isCompleted,
          comments: isNull(PMChecklistPayload, 'comments', ''),
          isActive: isNull(PMChecklistPayload, 'isActive', true),
          pk_uuid: isNull(PMChecklistPayload, 'pk_uuid', uuidv4()),
          pkAssetInspectionPMChecklist: isNull(
            PMChecklistPayload,
            'pkAssetInspectionPMChecklist',
            0,
          ),
          dateCreated: newDate.toISOString(),
          site: SiteDiscipline.siteId,
          discipline: SiteDiscipline.disciplineId,
          inspectorId: Number(getValueFromLocalStorage('inspectorId')),
          fkAssetInspectionTask: CurrentUserSelected.taskId,
        };
        saveResult = await savePmChecklistCollection?.upsert(savePmChecklistRecord);
        return saveResult;
      }
    } catch (error) {
      sentryCaptureException(
        error,
        'file: useSavePmChecklist.ts ~ line 76 ~ savePmChecklistTransactionData',
      );
    }
  };

  return { pmChecklistTransactionData: result, savePmChecklistTransactionData };
};

export default useSavePmChecklist;
